<!-- <template>
  <div class="payment-form">
    <div class="form-group">
      <label>Card Number</label>
      <div ref="cardNumberElement" class="stripe-element"></div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label>Expiration </label>
        <div ref="cardExpiryElement" class="stripe-element"></div>
      </div>

      <div class="form-group">
        <label>CVC</label>
        <div ref="cardCvcElement" class="stripe-element"></div>
      </div>
    </div>

    <button
      class="blue-button w-100"
      @click="submitPayment"
      :disabled="processing"
    >
      {{ processing ? 'Processing...' : 'Subscribe' }}
    </button>
    <div class="trust-row">
      <span>🔒 Powered by Stripe</span>
      <span>🛡️ Privacy First</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { useMyStore } from '@/store.js';

const props = defineProps({
  publishableKey: String,
  selectedPlan: {
    type: String,
    default: 'monthly',
    validator: (value) => ['monthly', 'annual'].includes(value),
  },
});
const store = useMyStore();
const emit = defineEmits(['payment-success']);

const cardNumberElement = ref(null);
const cardExpiryElement = ref(null);
const cardCvcElement = ref(null);

const stripe = ref(null);
const elements = ref(null);
const cardElement = ref(null);
const processing = ref(false);
const error = ref('');
const monthlyPriceId = import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY;
const annualPriceId = import.meta.env.VITE_STRIPE_PRICE_ID_ANNUAL;

onMounted(async () => {
  try {
    stripe.value = await loadStripe(props.publishableKey);
    elements.value = stripe.value.elements();

    const style = {
      base: {
        fontSize: '16px',
        color: '#32325d',
      },
    };

    cardElement.value = elements.value.create('cardNumber', { style });
    cardElement.value.mount(cardNumberElement.value);
    elements.value
      .create('cardExpiry', { style })
      .mount(cardExpiryElement.value);
    elements.value.create('cardCvc', { style }).mount(cardCvcElement.value);
  } catch (err) {
    error.value = 'Failed to load payment form';
    console.error('Stripe error:', err);
  }
});

const submitPayment = async () => {
  try {
    processing.value = true;
    error.value = '';

    if (!stripe.value || !elements.value) {
      throw new Error('Payment system not ready');
    }

    const { paymentMethod, error: stripeError } =
      await stripe.value.createPaymentMethod({
        type: 'card',
        card: cardElement.value,
        billing_details: {
          email: store.user.email,
        },
      });

    if (stripeError) throw stripeError;

    const result = await store.startSubscription({
      paymentMethodId: paymentMethod.id,
      email: store.user.email,
      name: store.user.firstName + ' ' + store.user.lastName,
      priceId: props.selectedPlan === 'annual' ? annualPriceId : monthlyPriceId,
    });

    if (result.requiresAction) {
      const { error: confirmError } = await stripe.value.confirmCardPayment(
        result.clientSecret,
        {
          payment_method: {
            card: cardElement.value,
            billing_details: {
              email: store.user.email,
            },
          },
        }
      );
      if (confirmError) throw confirmError;
    }

    await store.getUserAccountData();

    emit('payment-success');
  } catch (err) {
    error.value = err.message || 'Payment failed';
    console.error('Full error:', err);
  } finally {
    processing.value = false;
  }
};
</script>

<style scoped>
.payment-form {
  max-width: 500px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.stripe-element {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.trust-row {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-size: 12px;
  color: #6b7280;
  margin-top: 20px;
}
</style> -->

<template>
  <div class="payment-form">
    <div v-if="isIosApp">
      <button class="blue-button w-100" @click="presentIosPaywall">
        Upgrade to Premium
      </button>
    </div>
    <!-- <div v-if="isIosApp">
      <h2 class="text-center mb-1">Upgrade on the Lucky Friday Web App</h2>
      <p class="small-grey-text mt-1">
        For now, subscriptions are managed on the Lucky Friday web app.
      </p>
      <p class="mt-1">
        To subscribe to <strong>Lucky Friday Premium</strong>, open your browser
        on your phone or computer and go to:
      </p>
      <p class="mt-1 url-text">
        <strong>luckyfridayapp.com</strong>
      </p>
      <p class="mt-1">
        Sign in with the same email you use in this app. Once you upgrade, your
        Premium features will automatically appear here.
      </p>
    </div> -->

    <div v-else>
      <div class="form-group">
        <label>Card Number</label>
        <div ref="cardNumberElement" class="stripe-element"></div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Expiration</label>
          <div ref="cardExpiryElement" class="stripe-element"></div>
        </div>

        <div class="form-group">
          <label>CVC</label>
          <div ref="cardCvcElement" class="stripe-element"></div>
        </div>
      </div>
      <div class="form-group">
        <label>Promo Code </label>
        <input
          v-model.trim="promoCode"
          class="text-input stripe-element promo-input"
          placeholder="enter promo code"
          autocomplete="off"
          @input="onPromoInput"
        />
        <p v-if="promoStatus === 'validating'" class="promo-status promo-muted">
          Checking…
        </p>
        <p v-else-if="promoStatus === 'valid'" class="promo-status promo-valid">
          ✓ {{ appliedDiscount.percentOff }}% off applied<span
            v-if="appliedDiscount.duration === 'forever'"
          >
            — forever</span
          ><span v-else-if="appliedDiscount.duration === 'repeating'">
            for {{ appliedDiscount.durationInMonths }} months</span
          >
        </p>
        <p
          v-else-if="promoStatus === 'invalid'"
          class="promo-status promo-invalid"
        >
          Code not valid
        </p>
        <!-- <input
          v-model.trim="promoCode"
          class="text-input stripe-element promo-input"
          placeholder="FOUNDERS25"
          autocomplete="off"
        /> -->
      </div>

      <button
        class="blue-button w-100"
        @click="submitPayment"
        :disabled="processing"
        type="button"
      >
        {{ processing ? 'Processing...' : 'Subscribe' }}
      </button>

      <div class="trust-row">
        <span>🔒 Powered by Stripe</span>
        <span>🛡️ Privacy First</span>
      </div>

      <p v-if="error" class="error-text mt-1">
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { Capacitor } from '@capacitor/core';
import { useMyStore } from '@/store.js';
import {
  RevenueCatUI,
  PAYWALL_RESULT,
} from '@revenuecat/purchases-capacitor-ui';

const props = defineProps({
  publishableKey: String,
  selectedPlan: {
    type: String,
    default: 'monthly',
    validator: (value) => ['monthly', 'annual'].includes(value),
  },
});

// const emit = defineEmits(['payment-success', 'dismiss']);
const emit = defineEmits(['payment-success', 'dismiss', 'discount-applied']);

const store = useMyStore();

const cardNumberElement = ref(null);
const cardExpiryElement = ref(null);
const cardCvcElement = ref(null);

const stripe = ref(null);
const elements = ref(null);
const cardElement = ref(null);
const processing = ref(false);
const error = ref('');
const isIosApp = ref(false);
const promoCode = ref('');

const monthlyPriceId = import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY;
const annualPriceId = import.meta.env.VITE_STRIPE_PRICE_ID_ANNUAL;

const promoStatus = ref('idle'); // idle | validating | valid | invalid
const appliedDiscount = ref(null);
let debounceTimer = null;

onMounted(async () => {
  // const ref = localStorage.getItem('ref');
  // if (ref && !promoCode.value) {
  //   promoCode.value = ref;
  //   console.log('[Referral] auto-applied promo:', ref);
  // }

  const savedRef = localStorage.getItem('ref');
  if (savedRef && !promoCode.value) {
    promoCode.value = savedRef;
    validatePromo(savedRef);
  }

  if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
    isIosApp.value = true;
    return;
  }

  try {
    stripe.value = await loadStripe(props.publishableKey);
    elements.value = stripe.value.elements();

    const style = {
      base: {
        fontSize: '16px',
        color: '#32325d',
      },
    };

    cardElement.value = elements.value.create('cardNumber', { style });
    cardElement.value.mount(cardNumberElement.value);

    elements.value
      .create('cardExpiry', { style })
      .mount(cardExpiryElement.value);

    elements.value.create('cardCvc', { style }).mount(cardCvcElement.value);
  } catch (err) {
    error.value = 'Failed to load payment form';
    console.error('Stripe error:', err);
  }
});

const onPromoInput = () => {
  promoStatus.value = 'idle';
  appliedDiscount.value = null;
  emit('discount-applied', null);

  clearTimeout(debounceTimer);
  if (!promoCode.value) return;

  debounceTimer = setTimeout(() => validatePromo(promoCode.value), 500);
};

const validatePromo = async (code) => {
  if (!code) return;
  promoStatus.value = 'validating';
  console.log('before validating call in store');

  const data = await store.validatePromoCode(code);

  console.log('after validating store call');

  if (data.valid) {
    appliedDiscount.value = data;
    promoStatus.value = 'valid';
    emit('discount-applied', data);
  } else {
    appliedDiscount.value = null;
    promoStatus.value = 'invalid';
    emit('discount-applied', null);
  }
};

const submitPayment = async () => {
  try {
    processing.value = true;
    error.value = '';

    if (!stripe.value || !elements.value) {
      throw new Error('Payment system not ready');
    }

    const { paymentMethod, error: stripeError } =
      await stripe.value.createPaymentMethod({
        type: 'card',
        card: cardElement.value,
        billing_details: {
          email: store.user.email,
        },
      });

    if (stripeError) throw stripeError;
    console.log('promo code ' + promoCode.value);

    const result = await store.startSubscription({
      paymentMethodId: paymentMethod.id,
      email: store.user.email,
      name: store.user.firstName + ' ' + store.user.lastName,
      priceId: props.selectedPlan === 'annual' ? annualPriceId : monthlyPriceId,
      promoCode: promoCode.value || null,
    });

    if (result.requiresAction) {
      const { error: confirmError } = await stripe.value.confirmCardPayment(
        result.clientSecret,
        {
          payment_method: {
            card: cardElement.value,
            billing_details: {
              email: store.user.email,
            },
          },
        }
      );

      if (confirmError) throw confirmError;
    }

    await store.getUserAccountData();
    emit('payment-success');
  } catch (err) {
    if (err.response?.data?.error) error.value = err.response.data.error;
    else if (err.response?.data?.ErrorMessage)
      error.value = err.response.data.ErrorMessage;
    else error.value = err.message || 'Payment failed';
    console.error('Full error:', err);
  } finally {
    processing.value = false;
  }
};

const presentIosPaywall = async () => {
  try {
    const { result } = await RevenueCatUI.presentPaywall();
    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
      case PAYWALL_RESULT.RESTORED:
        await store.activateIapSubscription();
        await store.getUserAccountData();
        emit('payment-success');
        break;
      default:
        break;
    }
  } catch (e) {
    console.error('Paywall error:', e);
  }
};
</script>

<style scoped>
.payment-form {
  max-width: 500px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.stripe-element {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.trust-row {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-size: 12px;
  color: #6b7280;
  margin-top: 20px;
}

.error-text {
  color: #b91c1c;
  font-size: 0.875rem;
}

.url-text {
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.promo-input {
  width: 100%;
  box-sizing: border-box;
}

.promo-status {
  font-size: 0.8rem;
  margin-top: 0.4rem;
  margin-bottom: 0;
}
.promo-muted {
  color: #888;
}
.promo-valid {
  color: #0c9e2a;
  font-weight: 600;
}
.promo-invalid {
  color: #b91c1c;
}
</style>
